import './wdyr';
import App from './App';
import SwiperCore from 'swiper';
import { EffectCoverflow, Pagination, Mousewheel } from 'swiper/modules';
import getApolloClient from 'graph_ql/index';
import theme from './constants/themes';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { CookiesProvider } from 'react-cookie';
import { persistor, store } from './redux/store';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@material-ui/core';
import AuthProvider from 'contexts/AuthContext';
import { AlgoliaProvider } from 'features/algolia';
import { ConfigurationProvider } from 'features/configuration';
import { SyncRouterProvider } from 'features/router';
import { AnalyticProvider } from './routes/AnalyticProvider';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { useState } from 'react';
import { PackComponentType, WithChildrenProps, createPack } from 'react-component-pack';

import './styles/globals.css';
import 'swiper/css/bundle';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'react-toastify/dist/ReactToastify.css';
import utils, { isValidIp } from 'utils/utils';
import { history } from 'index';
import BugSnagFallbackError from './containers/error/BugSnagFallbackError';
import { TRACK_EVENT, trackEvent } from './utils/analytics';

const shopUrl = new URL(process.env.REACT_APP_APOLLO_HOST!);
const { host } = shopUrl;
const isLocal = host.includes('localhost') || isValidIp(host);

const bugSnagAPIKey = process.env.REACT_APP_BUGSNAG_API_KEY;
Bugsnag.start({
    apiKey: bugSnagAPIKey!,
    plugins: [new BugsnagPluginReact(React)],
    appVersion: process.env.REACT_APP_WEBAPP_VERSION || 'development',
    releaseStage: isLocal ? 'development' : process.env.REACT_APP_PROJECT_ID,
});

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)!;

SwiperCore.use([EffectCoverflow, Pagination, Mousewheel]);
const ProviderPack = createPack(
    CookiesProvider as PackComponentType<WithChildrenProps>,
    (props) => <Router {...props} history={history} />,
    (props) => <Provider {...props} store={store} />,
    (props) => <PersistGate {...props} loading={null} persistor={persistor} />,
    (props) => <ApolloProvider {...props} client={getApolloClient()} />,
    AlgoliaProvider,
    AuthProvider,
    SyncRouterProvider,
    ConfigurationProvider,
    (props) => <AnalyticProvider {...props} store={store} />,
    (props) => <ThemeProvider {...props} theme={theme} />,
);

const TaffiAppProvider = (props) => {
    const [latestVersion, setLatestVersion] = useState(process.env.REACT_APP_WEBAPP_VERSION);

    const onBugsnagError = (event) => {
        trackEvent(TRACK_EVENT.BUGSNAG_ERROR_CAUGHT, {
            errors: event.errors,
        });
        if (event.errors && event.errors?.length) {
            for (const error of event.errors) {
                utils.handleChunkLoadingError(latestVersion, setLatestVersion, error);
            }
        }
    };

    return (
        <ErrorBoundary onError={onBugsnagError} FallbackComponent={BugSnagFallbackError}>
            <ProviderPack>
                <App />
            </ProviderPack>
        </ErrorBoundary>
    );
};

export default TaffiAppProvider;
